import React from 'react';
import { useMobile } from '../../../../hooks/useMobile';
import {
  useGetStartedContext,
  useEventTrackerContext,
  useAuthContext,
  useOnboardingContext,
} from '../../../../contexts';
import { useSignupContext } from './SignupProvider';
import { useHistory } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { signupValidations } from './signup-validation';
import {
  pathname,
  forwardUrlParams,
  login,
  saveSignUpData,
  getUrlSignUpParams,
  addSignUpDataToLead,
  logger,
} from '../../../../utils';
import { signUp } from './utils';
import { SignUpData } from '../../../../Types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import NextExitButtons from '../../../BuildingBlocks/GetStarted/NextExitButtons';
import PrivacyPolicy from './PrivacyPolicy';
import Banner from '../../../BuildingBlocks/GetStarted/Banner';
import GetStartedHeader from '../../../BuildingBlocks/Headers/GetStartedHeader';
import SignupFields from './SignupFields';
import PageError from '../../../auth/PageError';
import SignInGoogle from '../../../auth/SignInGoogle';
import TextDivider from '../../../BuildingBlocks/Dividers/TextDivider';
import { SIGNUP_HEADER, SIGNUP_SUB_HEADER } from '../../messages';

const useStyles = makeStyles((theme: any) => ({
  container: {
    backgroundColor: theme.palette.brand.white,
    justifyContent: 'center',
    textAlign: 'center',
    alignSelf: 'center',
    maxWidth: 750,
    padding: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      maxWidth: 350,
    },
  },
  google: { marginLeft: 'auto', marginRight: 'auto', maxWidth: 300 },
}));

const USER_EXISTS_ERROR = 'UsernameExistsException';

export default function Signup({
  handlePostSignUp,
  enableGoogleSignIn = true,
  enableBack = true,
}: {
  handlePostSignUp: (...args) => Promise<void>;
  enableGoogleSignIn?: boolean;
  enableBack?: boolean;
}) {
  const mobile = useMobile();
  const classes = useStyles();
  const history = useHistory();
  const { trackEvent } = useEventTrackerContext();
  const { signIn, setUser } = useAuthContext();
  const { email, passedMinRevenue, platform, leadId } = useGetStartedContext();
  const { loading, setLoading, generalError, setGeneralError } =
    useSignupContext();
  const { setActiveStep, setHasCompletedOnboarding } = useOnboardingContext();

  const formOptions = {
    resolver: yupResolver(signupValidations),
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    setError,
  } = useForm(formOptions);

  const emailWatch = useWatch({ control, name: 'email' });
  const nameTermsWatch = useWatch({ control, name: 'firstName' });
  const lastNameTermsWatch = useWatch({ control, name: 'lastName' });
  const acceptedTermsWatch = useWatch({ control, name: 'acceptedTerms' });
  const passwordWatch = useWatch({ control, name: 'password' });
  const passwordConfirmWatch = useWatch({ control, name: 'passwordConfirm' });
  const phoneNumberWatch = useWatch({ control, name: 'phoneNumber' });

  const disableContinueButton =
    !emailWatch ||
    !nameTermsWatch ||
    !lastNameTermsWatch ||
    !acceptedTermsWatch ||
    !passwordWatch ||
    !passwordConfirmWatch ||
    !phoneNumberWatch;

  async function submit(answers: SignUpData) {
    const logData = { email: answers?.email, phone: answers?.phoneNumber };
    logger.info({
      message: 'Starting signup',
      data: logData,
    });
    try {
      setLoading(true);
      const { email, phoneNumber, firstName, lastName, password } = answers;
      const urlParams = await getUrlSignUpParams();
      await Promise.all([
        addSignUpDataToLead(answers, urlParams),
        signUp({
          email,
          password,
          firstName,
          lastName,
          tel: phoneNumber?.replace(/[()\-\s]/g, ''),
          findUs: null,
          urlParams,
          trackEvent,
        }),
      ]);
      await login({
        email,
        password,
        signIn,
        history,
        setUser,
        setHasCompletedOnboarding,
        setActiveStep,
        runSignIn: false,
      });
      await handlePostSignUp({ passedMinRevenue, leadId, platform });
      setLoading(false);
      signIn(email);
      saveSignUpData();
    } catch (error) {
      setLoading(false);
      if (error.code === USER_EXISTS_ERROR) {
        setError('email', { message: error.message });
      } else {
        setGeneralError(error.message);
      }
      logger.error({ message: 'normal_signup_error', error, data: logData });
    }
    logger.info({
      message: 'Finished signup',
      data: logData,
    });
  }

  const backClick = enableBack
    ? () =>
        history.push({
          pathname: pathname.preLogin.getStarted.salesRevenue.url,
          search: forwardUrlParams(),
        })
    : null;

  return (
    <>
      <Banner />
      <div className={classes.container}>
        <GetStartedHeader
          mobile={mobile}
          header={SIGNUP_HEADER}
          subheader={SIGNUP_SUB_HEADER}
        />

        <div>
          {enableGoogleSignIn && (
            <div className={classes.google}>
              <SignInGoogle
                large={true}
                getStartedData={{
                  passedMinRevenue,
                  platform,
                  leadId,
                }}
              />
              <TextDivider />
            </div>
          )}
          <form onSubmit={handleSubmit(submit)}>
            <SignupFields
              setValue={setValue}
              errors={errors}
              register={register}
              email={email}
              emailWatch={emailWatch}
            />
            <PrivacyPolicy
              inputProps={{
                ...register('acceptedTerms'),
              }}
              acceptedTerms={'acceptedTerms'}
              error={errors?.acceptedTerms}
            />
            <PageError error={generalError} />
            <NextExitButtons
              backClick={backClick}
              disable={loading || disableContinueButton}
              loading={loading}
            />
          </form>
        </div>
      </div>
    </>
  );
}
